import { createContext, FC, useContext, useEffect, useState } from 'react';

import { emptyPermissions, GetPermissions, Permissions } from '../data/PermissionData';
import useAuth from '../auth/UseAuth';
import { appSettings } from '../AppSettings';
import { ALL_PERMISSIONS } from '../auth/AuthPermission';

export type UserPermissions = Permissions & {
  isAuthenticated?: boolean | undefined;
};

const PERMISSION_FLAGS = {
  hasAdminAccess: ALL_PERMISSIONS.ADMIN,
  hasDecryptForeignHashesAccess: ALL_PERMISSIONS.DECRYPT_FOREIGN_HASHES,
  hasGreenSideViewAccess: ALL_PERMISSIONS.GREEN_SIDE_VIEW,
  hasGreenSideWriteAccess: ALL_PERMISSIONS.GREEN_SIDE_WRITE,
  hasSampleTrackingViewAccess: ALL_PERMISSIONS.SAMPLE_TRACKING_VIEW,
  hasSampleTrackingWriteAccess: ALL_PERMISSIONS.SAMPLE_TRACKING_WRITE,
  hasSuperDuperFiestaAccess: ALL_PERMISSIONS.SDF_USER,
  hasSuperDuperFiestaAdminAccess: ALL_PERMISSIONS.SDF_ADMIN,
  hasSampleTrackingAdminAccess: ALL_PERMISSIONS.SAMPLE_TRACKING_ADMIN,
  hasRedSideAccess: ALL_PERMISSIONS.LL_USER,
} as const;

export const UserPermissionsContext = createContext<UserPermissions>({} as UserPermissions);

export const UserPermissionsProvider: FC = ({ children }) => {
  const { accessToken, user } = useAuth();

  const [userPermissions, setUserPermissions] = useState<UserPermissions>(emptyPermissions);

  useEffect(() => {
    const getPermissions = async () => {
      if (accessToken === undefined) {
        return;
      }

      try {
        const permissionData = await GetPermissions(accessToken);

        const permissions = permissionData.permissions;
        // user is "authenticated" if they have at least one permission
        const isAuthenticated = Object.values(permissions).some((value: boolean) => value);
        const result = { ...permissions, isAuthenticated };

        setUserPermissions(result);
      } catch (error: any) {
        // NOTE: this can and should be removed once the API is released and the endpoint is always available
        // It's primarily here to prevent issues using the UI with an older version of the API
        if (error.message === 'Error: not found.' && user) {
          // if the endpoint is not found, then the API most likely isn't compatible, so we'll fall back to setting the permissions from the token here
          const userPermissions = user[`${appSettings.auth.claims_namespace}/permissions`];
          if (!userPermissions.includes(ALL_PERMISSIONS.LL_USER)) {
            setUserPermissions({ ...emptyPermissions, isAuthenticated: false });
            return;
          }

          const computedPermissions: UserPermissions = Object.fromEntries(
            Object.entries(PERMISSION_FLAGS).map(([key, value]) => [key, userPermissions.includes(value)])
          ) as UserPermissions;

          setUserPermissions({
            ...computedPermissions,
            isAuthenticated: true,
          });
        }
      }
    };

    if (userPermissions.isAuthenticated === undefined) {
      getPermissions();
    }
  }, [accessToken, userPermissions, user]);

  return <UserPermissionsContext.Provider value={{ ...userPermissions }}>{children}</UserPermissionsContext.Provider>;
};

export const useUserPermissions = () => useContext(UserPermissionsContext);
